// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  MetricDivider,
  MetricRow,
  PrimaryMetric,
  SummaryCard,
} from '@/components/summary-card';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { FixedAssetCurrencyStats } from '@/report/api/get-fixed-assets-snapshot-report';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { Grid } from '@mui/material';

interface Props {
  stats: FixedAssetCurrencyStats;
  currencyCode: IsoCurrencyCode;
  isLoading: boolean;
}

/**
 * Renders the fixed-asset snapshot summary cards for a single cost-currency
 * bucket. Stats are computed by the backend per currency, so no total here ever
 * mixes currencies; amounts are formatted in the bucket's isoCode.
 */
export function FixedAssetSummaryCards(props: Props) {
  return (
    <Grid container spacing={3}>
      {/* Asset Overview Card */}
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Total fixed assets"
            value={formatNumber(props.stats.assetCount)}
            isLoading={props.isLoading}
          />
          <MetricDivider>
            <MetricRow
              label="Complete records"
              value={formatNumber(props.stats.assetsWithCompleteAccounting)}
              color="success.main"
              isLoading={props.isLoading}
            />
            <MetricRow
              label="Incomplete records"
              value={formatNumber(props.stats.assetsWithIncompleteAccounting)}
              color="error.main"
              isLoading={props.isLoading}
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>

      {/* Financial Summary Card */}
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Remaining book value"
            value={formatAmountWithCurrency(
              props.stats.totalBookValue,
              props.currencyCode,
              { sparse: true }
            )}
            isLoading={props.isLoading}
          />
          <MetricDivider>
            <MetricRow
              label="Depreciable amount"
              value={formatAmountWithCurrency(
                props.stats.totalDepreciableAmount,
                props.currencyCode,
                { sparse: true }
              )}
              isLoading={props.isLoading}
            />
            <MetricRow
              label="Accumulated depreciation"
              value={formatAmountWithCurrency(
                props.stats.totalAccumulatedDepreciation,
                props.currencyCode,
                { sparse: true }
              )}
              isLoading={props.isLoading}
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>

      {/* Performance Metrics Card */}
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Avg. in-service days"
            value={formatNumber(props.stats.weightedInServiceDays, {
              placeholder: '—',
            })}
            isLoading={props.isLoading}
            tooltip="Weighted average number of days in service (dollar-weighted by asset cost basis)"
          />
          <MetricDivider>
            <MetricRow
              label="Avg. useful life remaining"
              value={
                props.stats.weightedUsefulLifeRemaining !== null
                  ? pluralize(props.stats.weightedUsefulLifeRemaining, {
                      singular: 'day',
                      plural: 'days',
                    })
                  : '—'
              }
              isLoading={props.isLoading}
              tooltip="Weighted average remaining useful life of fixed assets (dollar-weighted by asset cost basis)"
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>
    </Grid>
  );
}
